import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { 
  Search, 
  Home, 
  MapPin, 
  BedDouble, 
  Bath, 
  Square,
  SlidersHorizontal,
  X,
  Grid3X3,
  List,
  GitCompare
} from "lucide-react";
import Navbar from "../components/Navbar";

const PropertiesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [compareIds, setCompareIds] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    city: searchParams.get("city") || "",
    property_type: searchParams.get("property_type") || "",
    listing_type: searchParams.get("listing_type") || "",
    status: searchParams.get("status") || "",
    min_price: searchParams.get("min_price") || "",
    max_price: searchParams.get("max_price") || "",
    bedrooms: searchParams.get("bedrooms") || "",
    bathrooms: searchParams.get("bathrooms") || "",
  });

  const [priceRange, setPriceRange] = useState([0, 5000000]);

  useEffect(() => {
    fetchProperties();
  }, [searchParams]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (value && value !== "all") params.append(key, value);
      });
      
      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data.properties);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching properties:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== "all") params.set(key, value);
    });
    if (priceRange[0] > 0) params.set("min_price", priceRange[0].toString());
    if (priceRange[1] < 5000000) params.set("max_price", priceRange[1].toString());
    setSearchParams(params);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      city: "",
      property_type: "",
      listing_type: "",
      status: "",
      min_price: "",
      max_price: "",
      bedrooms: "",
      bathrooms: "",
    });
    setPriceRange([0, 5000000]);
    setSearchParams(new URLSearchParams());
  };

  const formatPrice = (price, listingType) => {
    if (listingType === "rent") {
      return `K${price.toLocaleString()}/mo`;
    }
    return `K${price.toLocaleString()}`;
  };

  const toggleCompare = (e, propertyId) => {
    e.preventDefault();
    e.stopPropagation();
    setCompareIds((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : prev.length < 3
        ? [...prev, propertyId]
        : prev
    );
  };

  const filterContent = (
    <div className="space-y-6">
      {/* Location */}
      <div>
        <label className="block text-sm font-medium mb-2">Location</label>
        <Input
          placeholder="City or Zip Code"
          value={filters.city}
          onChange={(e) => setFilters({ ...filters, city: e.target.value })}
          className="h-11"
          data-testid="filter-city"
        />
      </div>

      {/* Property Type */}
      <div>
        <label className="block text-sm font-medium mb-2">Property Type</label>
        <Select 
          value={filters.property_type} 
          onValueChange={(value) => setFilters({ ...filters, property_type: value })}
        >
          <SelectTrigger className="h-11" data-testid="filter-property-type">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="house">House</SelectItem>
            <SelectItem value="apartment">Apartment</SelectItem>
            <SelectItem value="condo">Condo</SelectItem>
            <SelectItem value="land">Land</SelectItem>
            <SelectItem value="commercial">Commercial</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Listing Type */}
      <div>
        <label className="block text-sm font-medium mb-2">Listing Type</label>
        <Select 
          value={filters.listing_type} 
          onValueChange={(value) => setFilters({ ...filters, listing_type: value })}
        >
          <SelectTrigger className="h-11" data-testid="filter-listing-type">
            <SelectValue placeholder="Buy or Rent" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="sale">For Sale</SelectItem>
            <SelectItem value="rent">For Rent</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Status Filter */}
      <div>
        <label className="block text-sm font-medium mb-2">Property Status</label>
        <Select 
          value={filters.status} 
          onValueChange={(value) => setFilters({ ...filters, status: value })}
        >
          <SelectTrigger className="h-11" data-testid="filter-status">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            <SelectItem value="active">Available</SelectItem>
            <SelectItem value="sold">Sold</SelectItem>
            <SelectItem value="rented">Rented</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Price Range: K{priceRange[0].toLocaleString()} - K{priceRange[1].toLocaleString()}
        </label>
        <Slider
          value={priceRange}
          onValueChange={setPriceRange}
          min={0}
          max={5000000}
          step={50000}
          className="mt-4"
          data-testid="filter-price-slider"
        />
      </div>

      {/* Bedrooms */}
      <div>
        <label className="block text-sm font-medium mb-2">Bedrooms</label>
        <Select 
          value={filters.bedrooms} 
          onValueChange={(value) => setFilters({ ...filters, bedrooms: value })}
        >
          <SelectTrigger className="h-11" data-testid="filter-bedrooms">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
            <SelectItem value="5">5+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bathrooms */}
      <div>
        <label className="block text-sm font-medium mb-2">Bathrooms</label>
        <Select 
          value={filters.bathrooms} 
          onValueChange={(value) => setFilters({ ...filters, bathrooms: value })}
        >
          <SelectTrigger className="h-11" data-testid="filter-bathrooms">
            <SelectValue placeholder="Any" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any</SelectItem>
            <SelectItem value="1">1+</SelectItem>
            <SelectItem value="2">2+</SelectItem>
            <SelectItem value="3">3+</SelectItem>
            <SelectItem value="4">4+</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button 
          onClick={applyFilters} 
          className="flex-1 h-11 bg-primary text-primary-foreground active:scale-95 transition-all"
          data-testid="apply-filters-btn"
        >
          Apply Filters
        </Button>
        <Button 
          onClick={clearFilters} 
          variant="outline" 
          className="h-11 active:scale-95 transition-all"
          data-testid="clear-filters-btn"
        >
          Clear
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Header */}
      <section className="pt-24 pb-8 bg-muted">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-serif mb-2">Properties</h1>
          <p className="text-muted-foreground">
            {total} properties available
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters Sidebar */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 bg-card border border-border rounded-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-xl">Filters</h2>
                <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
              </div>
              {filterContent}
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {/* Mobile Filter Button */}
                <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <SheetTrigger asChild className="lg:hidden">
                    <Button variant="outline" className="h-10" data-testid="mobile-filters-btn">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[320px] overflow-y-auto">
                    <SheetHeader>
                      <SheetTitle className="font-serif">Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      {filterContent}
                    </div>
                  </SheetContent>
                </Sheet>

                <p className="text-sm text-muted-foreground">
                  Showing {properties.length} of {total} properties
                </p>
              </div>

              {/* View Toggle */}
              <div className="flex items-center gap-2 border border-border rounded-sm p-1">
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded-sm transition-all active:scale-90 ${
                    viewMode === "grid" ? "bg-primary text-white" : "hover:bg-muted"
                  }`}
                  data-testid="view-grid-btn"
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded-sm transition-all active:scale-90 ${
                    viewMode === "list" ? "bg-primary text-white" : "hover:bg-muted"
                  }`}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Properties Grid/List */}
            {loading ? (
              <div className={`grid gap-6 ${
                viewMode === "grid" 
                  ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" 
                  : "grid-cols-1"
              }`}>
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="bg-card rounded-sm overflow-hidden animate-pulse">
                    <div className={`bg-muted ${viewMode === "grid" ? "h-52" : "h-40"}`}></div>
                    <div className="p-5">
                      <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                      <div className="h-6 bg-muted rounded w-1/2 mb-4"></div>
                      <div className="h-4 bg-muted rounded w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : properties.length === 0 ? (
              <div className="text-center py-16">
                <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-serif text-xl mb-2">No Properties Found</h3>
                <p className="text-muted-foreground mb-6">Try adjusting your filters</p>
                <Button onClick={clearFilters} variant="outline">
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div className={`grid gap-6 ${
                viewMode === "grid" 
                  ? "grid-cols-1 md:grid-cols-2 xl:grid-cols-3" 
                  : "grid-cols-1"
              }`}>
                {properties.map((property, index) => (
                  <motion.div
                    key={property.property_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                  >
                    <Link
                      to={`/properties/${property.property_id}`}
                      className={`block property-card bg-card border border-border/40 rounded-sm overflow-hidden group ${
                        viewMode === "list" ? "flex" : ""
                      }`}
                      data-testid={`property-item-${property.property_id}`}
                    >
                      <div className={`relative overflow-hidden ${
                        viewMode === "grid" ? "h-52" : "w-64 h-40 flex-shrink-0"
                      }`}>
                        <img
                          src={property.images?.[0] || "https://images.unsplash.com/photo-1746458258536-b9ee5db20a73"}
                          alt={property.title}
                          className="w-full h-full object-cover property-image"
                        />
                        {/* Status badges */}
                        <div className="absolute top-3 left-3 flex gap-2">
                          {property.status === "rented" && property.also_for_sale ? (
                            <>
                              <span className="px-2 py-1 text-xs font-medium rounded-sm bg-orange-500 text-white">
                                RENTED
                              </span>
                              <span className="px-2 py-1 text-xs font-medium rounded-sm bg-green-600 text-white">
                                ALSO FOR SALE
                              </span>
                            </>
                          ) : (
                            <>
                              <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                                property.listing_type === "rent" 
                                  ? "bg-gold text-black" 
                                  : "bg-primary text-white"
                              }`}>
                                {property.listing_type === "rent" ? "For Rent" : "For Sale"}
                              </span>
                              {property.status === "rented" && (
                                <span className="px-2 py-1 text-xs font-medium rounded-sm bg-orange-500 text-white">
                                  RENTED
                                </span>
                              )}
                              {property.status === "sold" && (
                                <span className="px-2 py-1 text-xs font-medium rounded-sm bg-red-600 text-white">
                                  SOLD
                                </span>
                              )}
                            </>
                          )}
                        </div>
                        {/* Overlay for sold or rented (not also for sale) */}
                        {(property.status === "sold" || (property.status === "rented" && !property.also_for_sale)) && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <span className="text-white text-2xl font-bold tracking-wider opacity-80">
                              {property.status === "rented" ? "RENTED" : "SOLD"}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-5 flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 text-muted-foreground text-sm">
                            <MapPin className="w-3 h-3" />
                            {property.city}, {property.state}
                          </div>
                          {property.ref_number && (
                            <span className="text-xs font-mono bg-muted px-2 py-0.5 rounded">{property.ref_number}</span>
                          )}
                        </div>
                        <h3 className="font-serif text-lg mb-1 group-hover:text-primary transition-colors line-clamp-1">
                          {property.title}
                        </h3>
                        <p className="font-mono text-xl font-semibold text-primary mb-3">
                          {formatPrice(property.price, property.listing_type)}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground border-t border-border pt-3">
                          <span className="flex items-center gap-1">
                            <BedDouble className="w-3 h-3" /> {property.bedrooms}
                          </span>
                          <span className="flex items-center gap-1">
                            <Bath className="w-3 h-3" /> {property.bathrooms}
                          </span>
                          <span className="flex items-center gap-1">
                            <Square className="w-3 h-3" /> {property.area_sqft.toLocaleString()} sqft
                          </span>
                          <button
                            onClick={(e) => toggleCompare(e, property.property_id)}
                            className={`ml-auto flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                              compareIds.includes(property.property_id)
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted hover:bg-muted/80 text-muted-foreground"
                            }`}
                            data-testid={`compare-toggle-${property.property_id}`}
                          >
                            <GitCompare className="w-3 h-3" />
                            {compareIds.includes(property.property_id) ? "Added" : "Compare"}
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Floating Compare Bar */}
      {compareIds.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-50 px-4 py-3" data-testid="compare-bar">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <GitCompare className="w-5 h-5 text-primary shrink-0" />
              <span className="text-sm font-medium shrink-0">{compareIds.length}/3 selected</span>
              <div className="flex gap-2 overflow-x-auto">
                {compareIds.map((id) => {
                  const p = properties.find((x) => x.property_id === id);
                  return p ? (
                    <span key={id} className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-xs shrink-0">
                      {p.title?.substring(0, 20)}{p.title?.length > 20 ? "..." : ""}
                      <button onClick={() => setCompareIds((prev) => prev.filter((x) => x !== id))} className="hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setCompareIds([])}>Clear</Button>
              <Button
                size="sm"
                disabled={compareIds.length < 2}
                asChild={compareIds.length >= 2}
                data-testid="compare-now-btn"
              >
                {compareIds.length >= 2 ? (
                  <Link to={`/properties/compare?ids=${compareIds.join(",")}`}>Compare Now</Link>
                ) : (
                  <span>Select at least 2</span>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertiesPage;
